import { Button, Modal } from '@components/UI/Html';
import { NOTIFICATION_AUDIO_URL } from '@libs/constants/constants';
import { translate } from '@state/utils/helper';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import type { IconType } from 'react-icons/lib';
import './NotificationModal.scss';

const DEFAULT_FAVICON = '/favicon.ico';
const NOTIFICATION_FAVICON = '/favicon-notification.ico'; // Update this path to your red dot favicon

// Utility function to change favicon
const changeFavicon = (href: string) => {
  const link = document.querySelector("link[rel*='icon']");
  if (link) {
    (link as HTMLLinkElement).href = href;
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.href = href;
    document.head.appendChild(newLink);
  }
};

interface NotificationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onViewMessage: () => void;
  icon?: string | IconType;
  imageSrc?: string;
  title: string;
  message: string;
  buttonIcon?: string;
  buttonText?: string;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  toggle,
  onViewMessage,
  icon,
  title,
  message,
  buttonIcon,
  buttonText,
  imageSrc,
}) => {
  const [pickupInitiated, setPickupInitiated] = React.useState(false);
  function handlePickup() {
    setPickupInitiated(true);
    setTimeout(() => {
      toggle();
      onViewMessage();
    }, 900);
  }
  const notificationAudio = NOTIFICATION_AUDIO_URL;

  // Store original favicon and timeout refs
  const originalFaviconRef = useRef<string | null>(null);
  const faviconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Change favicon when modal opens
  useEffect(() => {
    if (isOpen) {
      // Get current favicon and store it
      const linkElement = document.querySelector("link[rel*='icon']");
      const currentFavicon = linkElement ? (linkElement as HTMLLinkElement).href : DEFAULT_FAVICON;
      if (!originalFaviconRef.current || originalFaviconRef.current === NOTIFICATION_FAVICON) {
        originalFaviconRef.current = currentFavicon;
      }

      // Change to notification favicon
      changeFavicon(NOTIFICATION_FAVICON);

      // Clear any existing timeout
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }

      // Revert favicon after 5 seconds
      faviconTimeoutRef.current = setTimeout(() => {
        if (originalFaviconRef.current) {
          changeFavicon(originalFaviconRef.current);
        }
        faviconTimeoutRef.current = null;
      }, 5000);
    } else {
      // Revert favicon when modal closes
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
      }
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
      }
    };
  }, [isOpen]);

  // Revert favicon on window focus (user is back to the tab)
  useEffect(() => {
    const handleFocus = () => {
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
        if (faviconTimeoutRef.current) {
          clearTimeout(faviconTimeoutRef.current);
          faviconTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      if (faviconTimeoutRef.current) {
        clearTimeout(faviconTimeoutRef.current);
        faviconTimeoutRef.current = null;
      }
      if (originalFaviconRef.current && originalFaviconRef.current !== NOTIFICATION_FAVICON) {
        changeFavicon(originalFaviconRef.current);
      }
    };
  }, []);

  return (
    <Modal
      id="notification-modal"
      size="md"
      isOpen={isOpen}
      toggle={toggle}
      className="custom__modal__gray"
      showCloseButton={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      {/* Add ping audio notification alert */}
      <audio src={notificationAudio} autoPlay style={{ display: 'none' }} />
      <div className="nf-container">
        <div className="nf-icon-container">
          {imageSrc ? (
            <img className="nf-image-profile" src={imageSrc} alt="icon" />
          ) : typeof icon === 'string' ? (
            <img className="nf-image" src={icon} alt="icon" />
          ) : (
            <div className="nf-icon">{icon && React.createElement(icon)}</div>
          )}
          <div className="nf-badge">1</div>
        </div>

        <h2 className="nf-heading">{title}</h2>
        <p className="nf-text">{message}</p>
        {buttonIcon && buttonText ? (
          <div
            className={classNames('nf-button-container', {
              'nf-button-container--pickup': pickupInitiated,
            })}
            onClick={handlePickup}
          >
            <div
              className={classNames('nf-button-icon-container', {
                'nf-button-icon-container--rotate': !pickupInitiated,
              })}
            >
              <img
                className={classNames(
                  'nf-button-icon',
                  pickupInitiated ? 'nf-button-icon--rotate' : ''
                )}
                src={buttonIcon}
                alt="icon"
              />
            </div>
            <div className="nf-button-text">{buttonText}</div>
          </div>
        ) : (
          <Button
            onClick={() => {
              toggle();
              onViewMessage();
            }}
            className="btn btn-primary mt-4"
          >
            {translate('takeProject.notificationModal.viewMessageBtn')}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default NotificationModal;
